import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./projectDetailsBar.scss";

interface ProjectDetailsBarProps {
  projectid?: string;
  projectName?: string;
  set_name?: string;
  showDetails?: boolean;
  handleProjectDetailsBar?: (isopen: boolean) => void;
  health?: string;
  pbtScore?: number | null;
  fromProjectReadiness?: boolean;
}

const ProjectDetailsBar: React.FC<ProjectDetailsBarProps> = ({
  projectid,
  projectName,
  set_name,
  showDetails,
  handleProjectDetailsBar,
  health,
  pbtScore,
  fromProjectReadiness = false,
}) => {
  const [isopen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenClose = () => {
    setIsOpen(!isopen);
    if (handleProjectDetailsBar) {
      handleProjectDetailsBar(isopen);
    }
  };

  const handleBackToDashboard = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/dashboard");
  };

  console.log("ProjectDetailsBar Props:", { 
    projectid, 
    projectName, 
    set_name, 
    showDetails,
    health, 
    pbtScore 
  });

  // More lenient check - show if we have projectid or projectName
  const hasProjectDetails = (projectid || projectName) && set_name;
  
  // Also show score/status if we have the data even without full project details
  const hasScoreOrStatus = (pbtScore !== null && pbtScore !== undefined) || health;

  // Normalize health status to lowercase for comparison
  const normalizedHealth = String(health || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isNA = normalizedHealth === "" || normalizedHealth === "na";

  // Get health indicator styles and content
  const getHealthIndicator = () => {
    if (isNA) {
      return {
        backgroundColor: "#d1d5db",
        color: "white",
        symbol: "–",
        tooltip: "Project Health is N/A",
      };
    }

    switch (normalizedHealth) {
      case "green":
        return {
          backgroundColor: "#4caf50",
          color: "white",
          symbol: "✓",
          tooltip: "Project Health is Green",
        };
      case "amber":
        return {
          backgroundColor: "#f59e0b",
          color: "white",
          symbol: "!",
          tooltip: "Project Health is Amber",
        };
      case "red":
        return {
          backgroundColor: "#ef4444",
          color: "white",
          symbol: "✕",
          tooltip: "Project Health is Red",
        };
      default:
        return {
          backgroundColor: "#d1d5db",
          color: "white",
          symbol: "–",
          tooltip: "Project Health is N/A",
        };
    }
  };

  const healthIndicator = getHealthIndicator();

  return (
    <div 
      className="projectDetailsBar-container" 
      style={{ 
        width: hasProjectDetails ? '90%' : '85%', 
        marginRight: hasProjectDetails ? '' : '160px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <div className="projectDetailsBar" onClick={handleOpenClose}>
        <div className="projectDetailsBar-div">
          {showDetails && (
            <>
              <div className="project-icon">
                <i className="fa-solid fa-building"></i>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <p style={{ 
                  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
                  color: 'black',
                  fontWeight: '600',
                  fontSize: '16px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  {fromProjectReadiness ? (
                    // Show only project name when from Project Readiness Dashboard (hide ID)
                    <>{projectName}</>
                  ) : (
                    // Show both ID and name when from normal Dashboard
                    <>{projectid} – {projectName}</>
                  )}
                </p>
                <p style={{ 
                  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
                  color: '#666',
                  fontWeight: '400',
                  fontSize: '13px',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Set: {set_name} | Last updated: {new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </>
          )}
        </div>
        
        {/* PBT Score and Overall Project Status - Show when we have score or status data */}
        {(hasProjectDetails || hasScoreOrStatus) && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px', 
            marginLeft: 'auto',
            paddingRight: '12px'
          }}>
            {/* PBT Assessment Score */}
            {pbtScore !== null && pbtScore !== undefined && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 700,
                  color: 'black',
                  whiteSpace: 'nowrap',
                  
                }}>
                  PBT Assessment Score:
                </span>
                <span
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color:'black',
                     
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(pbtScore * 100).toFixed(2)}%
                </span>
              </div>
            )}

            {/* Overall Project Status */}
            {health && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                title={healthIndicator.tooltip}
              >
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 600,
                  color: '#FF5E4F',
                  whiteSpace: 'nowrap',
                }}>
                  Overall Project Status:
                </span>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: healthIndicator.backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: healthIndicator.color,
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  }}
                >
                  {healthIndicator.symbol}
                </div>
              </div>
            )}
          </div>
        )}
        
        {hasProjectDetails && (
          <i className={`fa-solid fa-chevron-${isopen ? 'up' : 'down'}`} style={{ marginLeft: '12px' }}></i>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailsBar;
